// ./context/BlogModalContext.js
import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import BlogSelectModal from '../components/BlogSelectModal';
import { useBlog } from './BlogContext';
import { useAuth } from './AuthContext';

const BlogModalContext = createContext(null);

export const BlogModalProvider = ({ children }) => {
  const [showModal, setShowModal] = useState(false);
  const [onSelectCallback, setOnSelectCallback] = useState(() => () => {});

  const { blog, blogChecked } = useBlog();
  const { isAuthenticated, authChecked } = useAuth();

  // ✅ hard lock to prevent double-open before state updates land
  const openLockRef = useRef(false);

  const openBlogSelectModal = useCallback(
    (onSelect = () => {}) => {
      if (!authChecked || !isAuthenticated) return;
      if (!blogChecked) return;


      if (openLockRef.current) return;     // ✅ prevents the “twice” bug
      openLockRef.current = true;

      setOnSelectCallback(() => onSelect);
      setShowModal(true);
    },
    [authChecked, isAuthenticated, blogChecked, blog]
  );

  const closeBlogSelectModal = useCallback(() => {
    setShowModal(false);
    openLockRef.current = false;           // ✅ allow future opens if needed
  }, []);

  // ✅ Ensure selecting closes the modal first, then runs the caller callback
  const handleSelect = useCallback(
    async (selectedBlog) => {
      closeBlogSelectModal();
      try {
        await onSelectCallback(selectedBlog);
      } finally {
        // if the callback throws, we still want the modal closed + unlocked
        openLockRef.current = false;
      }
    },
    [onSelectCallback, closeBlogSelectModal]
  );

  const value = useMemo(
    () => ({ openBlogSelectModal, closeBlogSelectModal, showModal }),
    [openBlogSelectModal, closeBlogSelectModal, showModal]
  );

  return (
    <BlogModalContext.Provider value={value}>
      {children}
      {showModal && (
        <BlogSelectModal
          onClose={closeBlogSelectModal}
          onSelect={handleSelect} // ✅ wrapper
        />
      )}
    </BlogModalContext.Provider>
  );
};

export const useBlogModal = () => {
  const ctx = useContext(BlogModalContext);
  if (!ctx) throw new Error('useBlogModal must be used within a <BlogModalProvider>');
  return ctx;
};
